import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Bell, Bookmark, ChevronDown } from 'lucide-react'
import { useWatchlist } from '../contexts/WatchlistContext'
import type { WatchlistEntityType } from '../services/watchlist'
import { useToast } from '../contexts/ToastContext'
import WatchlistListPicker from './watchlist/WatchlistListPicker'
import WatchlistRemoveModal from './watchlist/WatchlistRemoveModal'
import WatchlistItemNotificationModal from './notifications/WatchlistItemNotificationModal'

interface WatchlistControlProps {
  entityType: WatchlistEntityType
  entityId: string
  displayName: string
  /** ISO country code when entity is foreign — stored like displayName, no extra CVR call. */
  countryCode?: string | null
  className?: string
  /** Compact bookmark icon for dense surfaces (e.g. affiliation cards). */
  iconOnly?: boolean
  /** Icon button size when `iconOnly` — `sm` for cards, `lg` for profile headers. */
  iconSize?: 'sm' | 'lg'
  /** Render the dropdown in a portal (avoids clipping inside overflow containers). */
  menuPortal?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Profile watchlist control: dropdown to add/edit list membership;
 * modal to confirm removal from one or more lists (or all).
 */
const WatchlistControl: React.FC<WatchlistControlProps> = ({
  entityType,
  entityId,
  displayName,
  countryCode = null,
  className = '',
  iconOnly = false,
  iconSize = 'sm',
  menuPortal = false,
  onOpenChange,
}) => {
  const {
    lists,
    isWatched,
    findItem,
    addItem,
    setItemLists,
    removeFromLists,
    removeItem,
    updateItemNotificationPreferences,
    createList,
  } = useWatchlist()
  const { showToast } = useToast()
  const [open, setOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [optimisticWatched, setOptimisticWatched] = useState<boolean | null>(null)
  const [draftListIds, setDraftListIds] = useState<number[]>([])
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)

  const watched = optimisticWatched ?? isWatched(entityType, entityId)
  const item = findItem(entityType, entityId)
  const dialogLabel = watched ? 'Rediger watchlist' : 'Tilføj til watchlist'
  const iconBtnClass =
    iconSize === 'lg' ? 'h-11 w-11' : 'h-6 w-6'
  const iconClass =
    iconSize === 'lg' ? 'h-6 w-6' : 'h-3.5 w-3.5'

  useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return
    setDraftListIds(item?.listIds ?? [])
  }, [open, item?.listIds])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open || !menuPortal) {
      setMenuPos(null)
      return
    }

    const updatePosition = () => {
      const btn = buttonRef.current
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.right })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, menuPortal])

  async function handleCreateList(name: string) {
    try {
      const list = await createList(name)
      showToast('Liste oprettet.')
      return list
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
      throw err
    }
  }

  async function handleSave() {
    const wasWatched = watched
    setOpen(false)
    if (!wasWatched) {
      setOptimisticWatched(true)
    }
    showToast(wasWatched ? 'Watchlist opdateret.' : 'Tilføjet til watchlist.')
    setBusy(true)
    try {
      if (wasWatched && item) {
        await setItemLists(item.id, draftListIds)
      } else {
        await addItem(entityType, entityId, displayName, draftListIds, countryCode)
      }
    } catch (err: unknown) {
      setOptimisticWatched(null)
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setBusy(false)
      setOptimisticWatched(null)
    }
  }

  const menu = open && (!menuPortal || menuPos) && (
    <div
      ref={menuRef}
      role="dialog"
      aria-label={dialogLabel}
      data-watchlist-menu
      style={
        menuPortal && menuPos
          ? {
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              transform: 'translateX(-100%)',
              zIndex: 9999,
            }
          : undefined
      }
      className={`w-56 max-w-[14rem] min-w-0 overflow-hidden bg-paper border border-rule shadow-lg rounded-sm p-3 ${
        menuPortal ? '' : `absolute right-0 top-full mt-1 ${iconOnly ? 'z-50' : 'z-30'}`
      }`}
    >
      {iconOnly && (
        <p className="text-xs font-semibold text-ink mb-2">{dialogLabel}</p>
      )}
      <WatchlistListPicker
        lists={lists}
        selectedListIds={draftListIds}
        onChange={setDraftListIds}
        idPrefix={`wl-${entityId}`}
        onCreateList={handleCreateList}
      />
      <div className="mt-3 flex flex-col gap-2 border-t border-rule pt-3">
        <button
          type="button"
          disabled={busy}
          onClick={handleSave}
          className="w-full bg-ink hover:bg-ink-700 disabled:bg-ink-300 text-paper text-xs font-semibold uppercase tracking-wider px-3 py-2 transition-colors"
        >
          {watched ? 'Gem' : busy ? 'Tilføjer…' : 'Tilføj til watchlist'}
        </button>
        {watched && item && (
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              setNotifyOpen(true)
            }}
            className="w-full text-left text-xs text-ink-600 hover:text-ink py-1 flex items-center gap-1.5"
          >
            <Bell className="h-3.5 w-3.5" aria-hidden />
            Notifikationer…
          </button>
        )}
        {watched && item && (
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              setRemoveOpen(true)
            }}
            className="w-full text-left text-xs text-signal hover:text-signal-700 py-1"
          >
            Fjern fra watchlist…
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div ref={wrapRef} className={`relative inline-flex items-center ${className}`}>
      <div className="inline-flex items-center gap-0.5">
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((v) => !v)
          }}
          disabled={busy}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={iconOnly ? dialogLabel : undefined}
          title={iconOnly ? dialogLabel : undefined}
          className={
            iconOnly
              ? `inline-flex ${iconBtnClass} items-center justify-center rounded-sm transition-colors disabled:opacity-50 ${
                  watched
                    ? 'text-signal hover:bg-signal-50'
                    : 'text-ink-400 hover:text-ink hover:bg-paper-200'
                }`
              : `inline-flex items-center gap-1 text-sm transition-colors disabled:opacity-50 ${
                  watched
                    ? 'text-signal font-medium'
                    : 'text-ink-500 hover:text-ink'
                }`
          }
        >
          <Bookmark
            className={`${iconOnly ? iconClass : 'h-4 w-4'} ${watched ? 'fill-current' : ''}`}
            aria-hidden
          />
          {!iconOnly && (
            <>
              <span className="hidden sm:inline">
                {watched ? 'På watchlist' : 'Watchlist'}
              </span>
              <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
            </>
          )}
        </button>
      </div>

      {menu && (menuPortal ? createPortal(menu, document.body) : menu)}

      {watched && item && item.notificationPreferences && (
        <WatchlistItemNotificationModal
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          displayName={displayName}
          entityType={entityType}
          preferences={item.notificationPreferences}
          onSave={async (updates) => {
            try {
              const updated = await updateItemNotificationPreferences(item.id, updates)
              showToast('Notifikationer opdateret.')
              return updated.notificationPreferences ?? { types: [] }
            } catch (err: unknown) {
              showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
              throw err
            }
          }}
        />
      )}

      {watched && item && (
        <WatchlistRemoveModal
          open={removeOpen}
          onClose={() => setRemoveOpen(false)}
          item={item}
          lists={lists}
          displayLabel={displayName}
          onRemoveFromLists={async (ids) => {
            await removeFromLists(item.id, ids)
            showToast('Opdateret.')
          }}
          onRemoveAll={async () => {
            await removeItem(item.id)
            showToast('Fjernet fra watchlist.')
          }}
        />
      )}
    </div>
  )
}

export default WatchlistControl
